import {
  AssetsResolverCSV,
  AssetsResolverV3CSV,
  entities,
  Entity,
  formatTicker,
} from '@toggle/toggle';

import { postMultipleEntities } from '~/services/entities/entity-service';
import { apiFetch } from '~/utils/api-fetch/apiFetch';

export type FormattedResolvedEntityData = {
  tags: string[];
  duplicates: string[];
  entities: AllEntitiesResolved[];
};

export type FormattedResolvedEntityV3Data = {
  tags: {
    primaryTag: string;
    alternativeTags: string[];
  }[];
  duplicates: string[];
  entities: {
    primaryEntity: AllEntitiesResolved;
    alternativeEntities: AllEntitiesResolved[];
  }[];
};

export type EntityResolved = Entity & {
  input: string;
};

export type EntityNotResolved = Partial<Entity> & {
  input: string;
};

export type AllEntitiesResolved = EntityResolved | EntityNotResolved;

export const CHAT_INPUT_ENTITIES_REGEX = /[\n\r,]/;

/**
 * DEPRECATED
 */
export const resolveEntitiesFromCsvV2 = async (maybeCsv: string) => {
  const csv = maybeCsv.trim().split(CHAT_INPUT_ENTITIES_REGEX).join(',');
  const res = await apiFetch<AssetsResolverCSV>(
    entities.resolveFromCsvV2.path,
    {
      method: 'post',
      body: {
        csv,
      },
    }
  );
  const tags = res?.data?.assets.filter(r => r.tag).map(r => r.tag);
  const inputs = res?.data?.assets.map(r => r.input);

  const defaultReturn: FormattedResolvedEntityData = {
    tags: [],
    duplicates: [],
    entities: [],
  };

  if (res.error || !tags?.length) {
    return {
      ...defaultReturn,
      entities: inputs?.map(input => ({
        input,
      })),
    } as FormattedResolvedEntityData;
  }

  const entitiesData = await postMultipleEntities(tags);
  const result = res.data.assets.reduce((acc, resolved) => {
    const entity = entitiesData.find(e => e.tag === resolved.tag);

    if (entity) {
      if (!acc.tags.includes(entity.tag)) {
        acc.tags.push(entity.tag);
        acc.entities.push({
          ...entity,
          input: resolved.input,
        });
      } else {
        const ticker = formatTicker(entity);
        acc.duplicates.push(ticker);
      }
    } else {
      acc.entities.push({
        input: resolved.input,
      });
    }

    return acc;
  }, defaultReturn);

  return result;
};

export const resolveEntitiesFromCsvV3 = async (maybeCsv: string) => {
  const preJoinedCsv = maybeCsv
    .trim()
    .split(CHAT_INPUT_ENTITIES_REGEX)
    .map(t => t.trim().toLowerCase())
    .filter(Boolean);
  const dedupedCsvList = Array.from(new Set(preJoinedCsv)).join(',');
  const res = await apiFetch<AssetsResolverV3CSV>(
    entities.resolveFromCsvV3.path,
    {
      method: 'post',
      body: { csv: dedupedCsvList },
    }
  );
  const { tags, inputs } = res?.data?.assets.reduce<{
    tags: string[];
    inputs: string[];
  }>(
    (acc, r) => {
      if (r.tags?.length) {
        acc.tags.push(...r.tags);
      }

      acc.inputs.push(r.input);
      return acc;
    },
    { tags: [], inputs: [] }
  ) || { tags: [], inputs: [] };

  const defaultReturn: FormattedResolvedEntityV3Data = {
    tags: [],
    duplicates: [],
    entities: [],
  };

  if (res.error || !tags?.length) {
    return {
      ...defaultReturn,
      entities: (inputs ?? []).map(input => ({
        primaryEntity: { input },
        alternativeEntities: [],
      })),
    } as FormattedResolvedEntityV3Data;
  }

  const entitiesData = await postMultipleEntities(tags);
  const entityByTag = new Map<string, Entity>();
  entitiesData.forEach(e => entityByTag.set(e.tag, e));

  const seenTags = new Set<string>();

  const result = res.data.assets.reduce<FormattedResolvedEntityV3Data>(
    (acc, resolved) => {
      const availableTags = (resolved.tags || []).filter(tag =>
        entityByTag.has(tag)
      );

      if (availableTags.length === 0) {
        acc.entities.push({
          primaryEntity: { input: resolved.input },
          alternativeEntities: [],
        });
        return acc;
      }

      const primaryTag = availableTags[0];
      const alternativeTags = availableTags.slice(1);

      const primaryEntityBase = entityByTag.get(primaryTag);
      const primaryEntity: AllEntitiesResolved = primaryEntityBase
        ? { ...primaryEntityBase, input: resolved.input }
        : { input: resolved.input };

      const alternativeEntities: AllEntitiesResolved[] = alternativeTags.map(
        t => {
          const e = entityByTag.get(t);
          return e
            ? { ...e, input: resolved.input }
            : { input: resolved.input };
        }
      );

      acc.tags.push({ primaryTag, alternativeTags });

      availableTags.forEach(tag => {
        const e = entityByTag.get(tag);

        if (e) {
          if (seenTags.has(tag)) {
            acc.duplicates.push(formatTicker(e));
          } else {
            seenTags.add(tag);
          }
        }
      });

      acc.entities.push({
        primaryEntity,
        alternativeEntities,
      });

      return acc;
    },
    defaultReturn
  );

  return result;
};
